import React, { useState, useEffect, useRef } from 'react';
import toast from 'react-hot-toast';
import { Plus, X, Upload, ImagePlus, Video, CheckCircle2, Star, Bold, Italic, AlignCenter } from 'lucide-react';
import AuthorAvatar from '../common/AuthorAvatar';
import { newsApi, footerApi } from '../../utils/api';
import { applyInlineFormat, toggleLinePrefix, BULLET_CHARS } from '../../utils/richText';

// पूरा समाचार formatting toolbar — mini green icons
function FormatToolbar({ onFormat }) {
  const btnCls =
    'inline-flex items-center justify-center w-7 h-7 rounded-md bg-emerald-50 border border-emerald-200 text-emerald-700 hover:bg-emerald-100 active:scale-95 transition-all';
  const glyphCls =
    'inline-flex items-center justify-center w-7 h-7 rounded-md bg-emerald-50 border border-emerald-200 text-emerald-700 hover:bg-emerald-100 active:scale-95 transition-all text-[13px] font-bold leading-none';

  return (
    <div className="flex items-center gap-1.5 flex-wrap p-1.5 rounded-lg bg-paper-soft border border-line">
      <button type="button" title="बोल्ड" aria-label="बोल्ड" className={btnCls} onClick={() => onFormat('bold')}>
        <Bold size={13} />
      </button>
      <button type="button" title="इटालिक" aria-label="इटालिक" className={btnCls} onClick={() => onFormat('italic')}>
        <Italic size={13} />
      </button>
      <button type="button" title="बीचमा राख्नुहोस्" aria-label="सेन्टर" className={btnCls} onClick={() => onFormat('center')}>
        <AlignCenter size={13} />
      </button>
      <span className="w-px h-5 bg-line mx-0.5" />
      {BULLET_CHARS.map((b) => (
        <button
          key={b}
          type="button"
          title={`बुलेट ${b}`}
          aria-label={`बुलेट ${b}`}
          className={glyphCls}
          onClick={() => onFormat(`bullet:${b}`)}
        >
          {b}
        </button>
      ))}
      <span className="text-[10.5px] text-muted ml-1">— शब्द छानेर वा कर्सर राखेर प्रयोग गर्नुहोस्</span>
    </div>
  );
}

function NewsForm({ newsId, categories, onCancel, onSave }) {
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    title: '',
    subtitle: '',
    excerpt: '',
    content: '',
    category_id: categories[0]?.id || '',
    author: '',
    authorImage: null,
    image: null,
    photos: [],
    video: null,
    date: new Date().toISOString().slice(0, 10),
    read_time: 3,
    featured: false,
  });
  const [photoPreviews, setPhotoPreviews] = useState([]);
  const [errors, setErrors] = useState({});
  // पूरा समाचार formatting toolbar का लागि textarea ref
  const contentRef = useRef(null);
  // Pahile upload gareko media (edit mode ma mini preview dekhauna matra)
  const [prevMedia, setPrevMedia] = useState({ image: null, photos: [], video: null });
  // X thichepachhi backend lai "hatau" bhanera pathaune flag
  const [removedMedia, setRemovedMedia] = useState({ image: false, video: false, authorImage: false });
  // टिम सदस्यहरू (लेखक dropdown) + पूर्वनिर्धारित लेखक — footer settings bata
  const [teamMembers, setTeamMembers] = useState([]);
  const [defaultAuthor, setDefaultAuthor] = useState({ name: 'कालो जोला', photo: '' });

  useEffect(() => {
    let mounted = true;
    footerApi
      .getSettings()
      .then((res) => {
        if (!mounted) return;
        try {
          const arr = JSON.parse(res.data?.team_members || '[]');
          if (Array.isArray(arr)) setTeamMembers(arr.filter((m) => m && String(m.name || '').trim()));
        } catch { /* ignore */ }
        try {
          const d = typeof res.data?.default_author === 'string'
            ? JSON.parse(res.data.default_author || '{}')
            : res.data?.default_author;
          if (d && (d.name || d.photo)) {
            setDefaultAuthor({ name: d.name || 'कालो जोला', photo: d.photo || '' });
          }
        } catch { /* ignore */ }
      })
      .catch(() => {});
    return () => { mounted = false; };
  }, []);

  // लेखक छान्दा (dropdown bata) — त्यही सदस्यको फोटो स्वतः भर्ने
  const handleAuthorSelect = (name) => {
    const member = teamMembers.find((m) => (m.name || '').trim() === name.trim());
    // Naya File/URL authorImage — dropdown bata photo auto-fill
    const photo = member?.photo || null;
    update('author', name);
    update('authorImage', photo);
    setRemovedMedia((r) => ({ ...r, authorImage: photo ? false : true }));
  };

  useEffect(() => {
    if (newsId) fetchNews();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newsId]);

  const fetchNews = async () => {
    setLoading(true);
    try {
      const response = await newsApi.getOne(newsId);
      const data = response.data;
      const photos = Array.isArray(data.photos) ? data.photos : [];
      setForm({
        title: data.title || '',
        subtitle: data.subtitle || '',
        excerpt: data.excerpt || '',
        content: data.content || '',
        category_id: data.category_id || '',
        author: data.author || '',
        authorImage: data.author_image || null,
        image: data.image || null,
        photos,
        video: data.video || null,
        date: data.date ? String(data.date).slice(0, 10) : new Date().toISOString().slice(0, 10),
        read_time: data.read_time || 3,
        featured: !!data.featured,
      });
      setPhotoPreviews(photos);
      setPrevMedia({ image: data.image || null, photos, video: data.video || null });
      setRemovedMedia({ image: false, video: false, authorImage: false });
    } catch (error) {
      console.error('Error fetching news:', error);
    } finally {
      setLoading(false);
    }
  };

  const update = (key, val) => setForm((f) => ({ ...f, [key]: val }));

  // Toolbar action — bold/italic/center/bullets sabai यहाँबाट apply हुन्छ
  const handleFormat = (type) => {
    const ta = contentRef.current;
    if (!ta) return;
    const { selectionStart: s, selectionEnd: e, value } = ta;

    let result;
    if (type === 'bold') {
      result = applyInlineFormat(value, s, e, '**');
    } else if (type === 'italic') {
      result = applyInlineFormat(value, s, e, '*');
    } else if (type === 'center') {
      result = toggleLinePrefix(value, s, e, '>> ');
    } else if (type.startsWith('bullet:')) {
      const char = type.slice(7);
      result = toggleLinePrefix(value, s, e, `${char} `);
    } else {
      return;
    }

    update('content', result.text);
    requestAnimationFrame(() => {
      ta.focus();
      ta.setSelectionRange(result.selectionStart, result.selectionEnd);
    });
  };

  const handleFileChange = (key, file) => {
    if (!file) return;
    // Naya file chhinepachhi "remove" flag clear
    setRemovedMedia((r) => ({ ...r, [key]: false }));
    update(key, file);
  };

  // X thichepachhi: preview hataune + backend lai remove pathaune
  const clearMedia = (key) => {
    update(key, null);
    setRemovedMedia((r) => ({ ...r, [key]: true }));
  };

  const handlePhotos = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const room = 2 - form.photos.length;
    if (room <= 0) {
      setErrors({ photos: 'बढीमा २ वटा फोटो मात्र अपलोड गर्न मिल्छ।' });
      e.target.value = '';
      return;
    }
    const toAdd = files.slice(0, room);
    const newPhotos = [...form.photos, ...toAdd];
    update('photos', newPhotos);
    setPhotoPreviews(newPhotos.map((f) => (f instanceof File ? URL.createObjectURL(f) : f)));
    e.target.value = '';
  };

  const removePhoto = (idx) => {
    const newPhotos = form.photos.filter((_, i) => i !== idx);
    update('photos', newPhotos);
    setPhotoPreviews(newPhotos.map((f) => (f instanceof File ? URL.createObjectURL(f) : f)));
  };

  const submit = async () => {
    if (!form.title.trim()) {
      setErrors({
        title: 'शीर्षक आवश्यक छ',
      });
      return;
    }
    setLoading(true);
    setErrors({});
    try {
      const formData = new FormData();
      Object.keys(form).forEach((key) => {
        if (key === 'photos' && Array.isArray(form[key])) {
          // Purano URL haru existingPhotos ma pathaune, naya File matra 'photos' ma
          const kept = form[key].filter((f) => !(f instanceof File));
          formData.append('existingPhotos', JSON.stringify(kept));
          form[key].forEach((file) => {
            if (file instanceof File) formData.append('photos', file);
          });
        } else if (['image', 'video', 'authorImage'].includes(key) && form[key] instanceof File) {
          formData.append(key, form[key]);
        } else if (key !== 'photos' && !['image', 'video', 'authorImage'].includes(key)) {
          // image/video/authorImage ko purano URL string pathaudaina —
          // backend le afai purano rakhchha, naya File matra pathainchha
          formData.append(key, form[key]);
        }
      });
      // Hatayeko media haru ko flag
      if (removedMedia.image && !form.image) formData.append('removeImage', '1');
      if (removedMedia.video && !form.video) formData.append('removeVideo', '1');
      // लेखकको फोटो — फोटो नछानिएको खण्डमा पूर्वनिर्धारित लेखकको लोगो फोटो प्रयोग गरिन्छ
      let authorImg = form.authorImage;
      if (!(authorImg instanceof File) && !authorImg && defaultAuthor.photo) authorImg = defaultAuthor.photo;
      if (typeof authorImg === 'string' && authorImg) {
        formData.append('author_image', authorImg);
        formData.delete('removeAuthorImage');
      } else if (removedMedia.authorImage && !(form.authorImage instanceof File) && !form.authorImage) {
        formData.append('removeAuthorImage', '1');
      }
      if (newsId) {
        await newsApi.update(newsId, formData);
        toast.success('समाचार अद्यावधिक भयो।');
      } else {
        await newsApi.create(formData);
        toast.success('समाचार प्रकाशित भयो।');
      }
      // Public pages lai turant refresh garauna
      window.dispatchEvent(new CustomEvent('kalonews:news-updated'));
      onSave();
    } catch (error) {
      console.error('Error saving news:', error);
      const msg = error.response?.data?.error || 'समाचार सुरक्षित गर्न असफल भयो।';
      setErrors({ submit: msg });
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  if (loading && newsId) {
    return <div className="bg-white border border-line rounded-[14px] p-5 text-muted">लोड हुँदै...</div>;
  }

  const labelCls = 'flex flex-col gap-1.5 text-[13px] font-semibold text-ink-soft';
  const inputCls = 'w-full border border-line rounded-lg px-3 py-2.5 text-sm font-normal text-ink outline-none bg-white focus:border-crimson';
  const fileToUrl = (v) => (v instanceof File ? URL.createObjectURL(v) : v);

  return (
    <div className="bg-white border border-line rounded-[14px] p-5 md:p-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <label className={`${labelCls} md:col-span-2`}>
          समाचार शीर्षक *
          <input className={inputCls} value={form.title} onChange={(e) => update('title', e.target.value)} placeholder="समाचारको मुख्य शीर्षक लेख्नुहोस्" />
          {errors.title && <div className="text-[12.5px] text-crimson font-semibold">{errors.title}</div>}
        </label>
        <label className={`${labelCls} md:col-span-2`}>
          उपशीर्षक
          <input className={inputCls} value={form.subtitle} onChange={(e) => update('subtitle', e.target.value)} placeholder="संक्षिप्त उपशीर्षक (वैकल्पिक)" />
        </label>
        <label className={`${labelCls} md:col-span-2`}>
          समाचार विवरण
          <textarea rows={3} className={`${inputCls} resize-y`} value={form.excerpt} onChange={(e) => update('excerpt', e.target.value)} placeholder="समाचारको संक्षिप्त विवरण..." />
        </label>
        <div className={`${labelCls} md:col-span-2`}>
          पूरा समाचार
          <FormatToolbar onFormat={handleFormat} />
          <textarea
            ref={contentRef}
            rows={8}
            className={`${inputCls} resize-y`}
            value={form.content}
            onChange={(e) => update('content', e.target.value)}
            placeholder="पूरा समाचार यहाँ लेख्नुहोस्... (माथिका बटनले बोल्ड, इटालिक, सेन्टर र बुलेट थप्न मिल्छ)"
          />
        </div>

        <label className={labelCls}>
          श्रेणी
          <select className={inputCls} value={form.category_id} onChange={(e) => update('category_id', e.target.value)}>
            {categories.map((c) => (
              <option key={c.id} value={c.id}>
                {/* समाज page entertainment category bata chalcha — label ma spasta dekhaune */}
                {c.slug === 'entertainment' ? 'समाज' : c.name}
              </option>
            ))}
          </select>
        </label>
        <label className={labelCls}>
          प्रकाशित मिति
          <input type="date" className={inputCls} value={form.date} onChange={(e) => update('date', e.target.value)} />
        </label>
        <label className={labelCls}>
          पढाइ समय (मिनेट)
          <input
            type="number"
            min="1"
            max="120"
            className={inputCls}
            value={form.read_time}
            onChange={(e) => update('read_time', e.target.value)}
            placeholder="३ मिनेट पढाइ"
          />
        </label>

        <label className={`${labelCls} md:col-span-2`}>
          <span className="inline-flex items-center gap-2 cursor-pointer select-none w-fit">
            <input
              type="checkbox"
              className="w-4 h-4 accent-crimson"
              checked={form.featured}
              onChange={(e) => update('featured', e.target.checked)}
            />
            <Star size={14} className="text-gold" fill="currentColor" />
            हिरो ब्यानर (गृहपृष्ठको मुख्य ब्यानरमा देखाउनुहोस्)
          </span>
        </label>

        <label className={labelCls}>
          लेखकको नाम
          <select
            className={inputCls}
            value={form.author}
            onChange={(e) => handleAuthorSelect(e.target.value)}
          >
            <option value="">
              -- पूर्वनिर्धारित ({defaultAuthor.name || 'कालो जोला'}) --
            </option>
            {teamMembers.map((m, i) => (
              <option key={i} value={m.name}>
                {m.name}{m.role ? ` — ${m.role}` : ''}
              </option>
            ))}
            {/* Dropdown ma nabhayeko purano लेखक name pani rakhne (edit mode) */}
            {form.author && !teamMembers.some((m) => (m.name || '').trim() === form.author.trim()) && (
              <option value={form.author}>{form.author} </option>
            )}
          </select>
          <span className="text-[11px] text-muted font-normal">
            टिम सदस्य छान्नुहोस् — फोटो स्वतः भरिनेछ। नछानी छोड़े पूर्वनिर्धारित लेखक ({defaultAuthor.name || 'कालो जोला'}) र लोगो फोटो स्वतः प्रयोग हुन्छ।
          </span>
        </label>
        <div className="flex items-center gap-2.5 flex-wrap pt-1">
          <AuthorAvatar name={form.author || defaultAuthor.name} imgSrc={fileToUrl(form.authorImage) || defaultAuthor.photo} size={44} />
          <span className="text-[12px] text-muted">
            {form.author
              ? `फोटो: ${form.authorImage ? 'स्वतः/छानिएको' : 'डिफल्ट लोगो'}`
              : `पूर्वनिर्धारित: ${defaultAuthor.name || 'कालो जोला'} (लोगो फोटो)`}
          </span>
          <label className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-ink-soft border border-dashed border-line rounded-lg px-3 py-2 cursor-pointer hover:border-crimson hover:text-crimson">
            <Upload size={14} /> लेखकको फोटो (वैकल्पिक)
            <input type="file" accept="image/*" hidden onChange={(e) => handleFileChange('authorImage', e.target.files[0])} />
          </label>
          {form.authorImage && (
            <button type="button" className="text-[12.5px] font-semibold text-crimson hover:underline" onClick={() => clearMedia('authorImage')}>
              हटाउनुहोस्
            </button>
          )}
        </div>

        <div className={`${labelCls} md:col-span-2`}>
          <span className="flex items-center gap-1.5 text-[13px] font-bold text-ink-soft">
            <ImagePlus size={15} /> थम्बनेल फोटो (मुख्य समाचारको फोटो)
          </span>
          {form.image ? (
            <div className="flex items-start gap-3 flex-wrap">
              {/* Preview ma click → naya photo; X → hataune */}
              <div className="relative block w-[160px] h-[100px] rounded-[10px] overflow-hidden border border-line group/th">
                <img src={fileToUrl(form.image)} alt="thumbnail" className="w-full h-full object-contain bg-paper-tint" />
                <label
                  htmlFor="nf-thumb-input"
                  className="absolute inset-0 bg-black/50 text-white text-[11px] font-bold flex items-center justify-center opacity-0 group-hover/th:opacity-100 transition-opacity cursor-pointer"
                >
                  फोटो बदल्नुहोस्
                </label>
                <button
                  type="button"
                  className="absolute top-1 right-1 z-10 w-5 h-5 rounded-full bg-black/60 text-white flex items-center justify-center"
                  onClick={() => clearMedia('image')}
                  aria-label="Remove thumbnail"
                >
                  <X size={12} />
                </button>
              </div>
            </div>
          ) : (
            <label htmlFor="nf-thumb-input" className="w-[160px] h-[100px] rounded-[10px] border border-dashed border-line text-muted flex items-center justify-center cursor-pointer hover:border-crimson hover:text-crimson">
              <Plus size={20} />
            </label>
          )}
          <input
            id="nf-thumb-input"
            type="file"
            accept="image/*"
            hidden
            onChange={(e) => {
              handleFileChange('image', e.target.files?.[0]);
              e.target.value = '';
            }}
          />
        </div>

        <div className={`${labelCls} md:col-span-2`}>
          <span className="flex items-center gap-1.5 text-[13px] font-bold text-ink-soft">
            <ImagePlus size={15} /> फोटो अपलोड (बढीमा २)
          </span>
          <div className="flex gap-2.5 flex-wrap">
            {photoPreviews.map((p, idx) => (
              <div key={idx} className="relative w-[84px] h-[84px] rounded-[10px] overflow-hidden border border-line">
                <img src={p} alt={`photo-${idx}`} className="w-full h-full object-cover" />
                <button
                  type="button"
                  className="absolute top-1 right-1 w-5 h-5 rounded-full bg-black/60 text-white flex items-center justify-center"
                  onClick={() => removePhoto(idx)}
                  aria-label="Remove"
                >
                  <X size={12} />
                </button>
              </div>
            ))}
            {form.photos.length < 2 && (
              <label className="w-[84px] h-[84px] rounded-[10px] border border-dashed border-line text-muted flex items-center justify-center cursor-pointer hover:border-crimson hover:text-crimson">
                <Plus size={18} />
                <input type="file" accept="image/*" hidden multiple onChange={handlePhotos} />
              </label>
            )}
          </div>
          {errors.photos && <div className="text-[12.5px] text-crimson font-semibold">{errors.photos}</div>}
        </div>

        <div className={`${labelCls} md:col-span-2`}>
          <span className="flex items-center gap-1.5 text-[13px] font-bold text-ink-soft">
            <Video size={15} /> भिडियो अपलोड (बढीमा २० सेकेन्ड)
          </span>
          {form.video ? (
            <div className="flex items-center gap-3 flex-wrap">
              {/* Video preview — controls chalu rakhna overlay le click block gardaina */}
              <div className="relative">
                <video src={fileToUrl(form.video)} controls className="w-[220px] rounded-[10px] max-h-[200px]" />
              </div>
              <label
                htmlFor="nf-video-input"
                className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-ink-soft border border-line rounded-lg px-3 py-2 cursor-pointer hover:border-crimson hover:text-crimson"
              >
                <Upload size={14} /> भिडियो बदल्नुहोस्
              </label>
              <button type="button" className="text-[12.5px] font-semibold text-crimson hover:underline" onClick={() => clearMedia('video')}>
                हटाउनुहोस्
              </button>
            </div>
          ) : (
            <label htmlFor="nf-video-input" className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-ink-soft border border-dashed border-line rounded-lg px-3 py-2 cursor-pointer hover:border-crimson hover:text-crimson w-fit">
              <Upload size={14} /> भिडियो छान्नुहोस्
            </label>
          )}
            <input
              id="nf-video-input"
              type="file"
              accept="video/*"
              hidden
              onChange={(e) => {
                handleFileChange('video', e.target.files?.[0]);
                e.target.value = '';
              }}
            />
        </div>

        {/* Pahile upload gareko media haru — mini size ma, tala */}
        {(prevMedia.image || prevMedia.photos.length > 0 || prevMedia.video) && (
          <div className="md:col-span-2 mt-2 pt-4 border-t border-dashed border-line">
            <span className="text-[12px] font-bold text-muted block mb-2">
              अपलोड गरिएका मिडिया (पहिलेका)
            </span>
            <div className="flex gap-2 flex-wrap">
              {prevMedia.image && (
                <div className="relative w-[64px] h-[64px] rounded-lg overflow-hidden border border-line">
                  <img src={prevMedia.image} alt="थम्बनेल" className="w-full h-full object-cover" />
                  <span className="absolute bottom-0 left-0 right-0 bg-black/55 text-white text-[8.5px] text-center py-0.5">थम्बनेल</span>
                </div>
              )}
              {prevMedia.photos.map((p, i) => (
                <div key={`p${i}`} className="relative w-[64px] h-[64px] rounded-lg overflow-hidden border border-line">
                  <img src={p} alt={`फोटो ${i + 1}`} className="w-full h-full object-cover" />
                  <span className="absolute bottom-0 left-0 right-0 bg-black/55 text-white text-[8.5px] text-center py-0.5">फोटो {i + 1}</span>
                </div>
              ))}
              {prevMedia.video && (
                <div className="relative w-[92px] h-[64px] rounded-lg overflow-hidden border border-line">
                  <video src={prevMedia.video} muted className="w-full h-full object-cover" />
                  <span className="absolute bottom-0 left-0 right-0 bg-black/55 text-white text-[8.5px] text-center py-0.5">भिडियो</span>
                </div>
              )}
            </div>
            <p className="text-[11px] text-muted mt-1.5">
              बदल्न चाहेमा माथिकै प्रिभ्यू/बटनमा क्लिक गरेर नयाँ फाइल छान्नुहोस् — पुरानो आफैँ बदलिन्छ।
            </p>
          </div>
        )}
      </div>

      {errors.submit && <div className="text-[12.5px] text-crimson font-semibold mt-3">{errors.submit}</div>}

      <div className="flex justify-end gap-2.5 mt-5">
        <button type="button" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-line text-[13px] font-semibold text-ink-soft hover:bg-paper-soft" onClick={onCancel}>
          रद्द गर्नुहोस्
        </button>
        <button type="button" className="inline-flex items-center gap-1.5 bg-crimson text-white font-semibold text-[13.5px] px-5 py-2.5 rounded-full hover:bg-crimson-dark disabled:opacity-60" onClick={submit} disabled={loading}>
          <CheckCircle2 size={15} /> {loading ? 'सुरक्षित गर्दै...' : 'समाचार प्रकाशित गर्नुहोस्'}
        </button>
      </div>
    </div>
  );
}

export default NewsForm;